import { useState, useEffect, useRef } from 'react';
import { Headset, X, PaperPlaneRight } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import { Input } from './ui/input';
import { Button } from './ui/button';
import api from '../lib/api';

export function SupportWidget() {
  const [hasSupport, setHasSupport] = useState(false);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    api.get('/support/status').then(res => {
      setHasSupport(res.data.has_support);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (open && hasSupport) loadChat();
  }, [open, hasSupport]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadChat = async () => {
    try {
      const res = await api.get('/support/chat');
      setMessages(res.data.messages || []);
    } catch (err) {}
  };

  const send = async () => {
    if (!input.trim() || sending) return;
    setSending(true);
    try {
      const res = await api.post('/support/chat/send', { content: input.trim() });
      setMessages(prev => [...prev, res.data]);
      setInput('');
    } catch (err) {}
    setSending(false);
  };

  if (!hasSupport) return null;

  return (
    <>
      {/* Toggle Button */}
      <button
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 left-6 z-50 w-12 h-12 bg-emerald-600 hover:bg-emerald-700 rounded-full flex items-center justify-center shadow-lg shadow-emerald-600/20 transition-transform hover:scale-110"
        data-testid="support-widget-toggle"
      >
        {open ? <X size={20} className="text-white" /> : <Headset size={22} className="text-white" weight="duotone" />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-20 left-6 z-50 w-80 bg-[#0D1321] border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
            data-testid="support-widget-panel"
          >
            {/* Header */}
            <div className="bg-emerald-600 p-4">
              <div className="flex items-center gap-2">
                <Headset size={20} className="text-white" weight="duotone" />
                <div>
                  <p className="text-sm font-bold text-white">24/7 Support</p>
                  <p className="text-[10px] text-emerald-100">Track My Package</p>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="h-64 overflow-y-auto p-3 space-y-2">
              {messages.length === 0 && (
                <div className="text-center mt-8">
                  <Headset size={32} className="text-gray-600 mx-auto mb-2" />
                  <p className="text-xs text-gray-500">How can we help?</p>
                  <p className="text-[10px] text-gray-600">Send a message and we'll respond ASAP.</p>
                </div>
              )}
              {messages.map(msg => (
                <div key={msg.id} className={`flex ${msg.sender === 'tenant' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] p-2.5 rounded-xl text-xs ${msg.sender === 'tenant' ? 'bg-emerald-600/20 text-emerald-200' : 'bg-white/[0.05] text-gray-300'}`}>
                    {msg.sender === 'admin' && <p className="text-[10px] text-emerald-400 font-medium mb-0.5">Support</p>}
                    <p>{msg.content}</p>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-3 border-t border-white/10 flex gap-2">
              <Input
                value={input}
                onChange={e => setInput(e.target.value)}
                placeholder="Type a message..."
                className="bg-white/5 border-white/10 text-white rounded-xl text-xs flex-1 h-9"
                onKeyDown={e => e.key === 'Enter' && send()}
                data-testid="support-message-input"
              />
              <Button onClick={send} disabled={sending} className="bg-emerald-600 hover:bg-emerald-700 rounded-xl h-9 w-9 p-0" data-testid="support-send-btn">
                <PaperPlaneRight size={14} className="text-white" />
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
